import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import "./themeUpdate.css";

const VALID_EXT = ["jpg", "jpeg", "png"];
const CHARACTER_MAX_FILE_MB = 2;
const BACKGROUND_MAX_FILE_MB = 5;
const MAX_CHARACTER_IMAGES = 15;
const CHARACTER_DIMENSIONS = [300, 300];
const DESKTOP_BG_DIMENSIONS = [1920, 1080];
const MOBILE_BG_DIMENSIONS = [414, 896];

const parseArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
};

const mergeNamesWithFiles = (existingNames, files) => {
  const uploadedNames = (files || []).map((file) => file?.name).filter(Boolean);
  return Array.from(new Set([...(existingNames || []), ...uploadedNames]));
};

/** Encode each path segment (decode first to avoid double-encoding stored keys). */
const buildHostedAssetUrl = (baseEnv, relativePath) => {
  const base = String(baseEnv ?? "").replace(/\/+$/, "");
  const raw = String(relativePath ?? "").trim().replace(/^\/+/, "");
  if (!raw) return "";
  const encoded = raw
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");
  return base ? `${base}/${encoded}` : encoded;
};

/** Prefix with numeric id so two uploads with same basename stay unique (merged list / server keys). */
const renameFileForUpload = (file) => {
  if (!file || !(file instanceof Blob)) return file;
  const original = file.name || "image";
  const lastDot = original.lastIndexOf(".");
  const ext = lastDot >= 0 ? original.slice(lastDot) : "";
  const base = lastDot >= 0 ? original.slice(0, lastDot) : original;
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  const newName = `${id}-${base}${ext}`;
  return new File([file], newName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified,
  });
};

function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [goodNames, setGoodNames] = useState([]);
  const [badNames, setBadNames] = useState([]);
  const [gameDeskName, setGameDeskName] = useState("");
  const [gameMobName, setGameMobName] = useState("");

  const [goodFiles, setGoodFiles] = useState([]);
  const [badFiles, setBadFiles] = useState([]);
  const [gameDeskFile, setGameDeskFile] = useState(null);
  const [gameMobFile, setGameMobFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const localAssets = useMemo(
    () => require.context("../../../img", false, /\.(png|jpg|jpeg|gif)$/),
    []
  );

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;

    const nextGood = parseArray(data.good);
    const nextBad = parseArray(data.bad);

    setGoodNames(nextGood);
    setBadNames(nextBad);
    setGameDeskName(data.game_background_desk || "");
    setGameMobName(data.game_background_mob || "");
  }, [data]);

  const [goodBlobSrcByName, setGoodBlobSrcByName] = useState({});
  const [badBlobSrcByName, setBadBlobSrcByName] = useState({});

  useLayoutEffect(() => {
    const next = {};
    goodFiles.forEach((f) => {
      if (f?.name) next[f.name] = URL.createObjectURL(f);
    });
    setGoodBlobSrcByName(next);
    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [goodFiles]);

  useLayoutEffect(() => {
    const next = {};
    badFiles.forEach((f) => {
      if (f?.name) next[f.name] = URL.createObjectURL(f);
    });
    setBadBlobSrcByName(next);
    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [badFiles]);

  const deskBlobSrc = useMemo(
    () => (gameDeskFile ? URL.createObjectURL(gameDeskFile) : ""),
    [gameDeskFile]
  );
  const mobBlobSrc = useMemo(
    () => (gameMobFile ? URL.createObjectURL(gameMobFile) : ""),
    [gameMobFile]
  );

  useEffect(() => {
    if (!deskBlobSrc) return undefined;
    return () => URL.revokeObjectURL(deskBlobSrc);
  }, [deskBlobSrc]);

  useEffect(() => {
    if (!mobBlobSrc) return undefined;
    return () => URL.revokeObjectURL(mobBlobSrc);
  }, [mobBlobSrc]);

  const getPreviewUrl = (fileName) => {
    if (!fileName) return "";
    try {
      return localAssets(`./${fileName}`);
    } catch {
      return buildHostedAssetUrl(process.env.REACT_APP_S3_PATH || "", fileName);
    }
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const src = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(src);
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        URL.revokeObjectURL(src);
        reject(new Error("Failed to read image dimensions"));
      };
      img.src = src;
    });

  const validateImageByType = async (file, type) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      return `Unsupported format for ${file.name}`;
    }

    const isCharacter = type === "good" || type === "bad";
    const maxSizeMb = isCharacter ? CHARACTER_MAX_FILE_MB : BACKGROUND_MAX_FILE_MB;
    if (file.size > maxSizeMb * 1024 * 1024) {
      return `${file.name} exceeds ${maxSizeMb}MB`;
    }

    const requiredDims =
      type === "good" || type === "bad"
        ? CHARACTER_DIMENSIONS
        : type === "gameDesk"
          ? DESKTOP_BG_DIMENSIONS
          : MOBILE_BG_DIMENSIONS;

    try {
      const { width, height } = await getImageDimensions(file);
      const [reqW, reqH] = requiredDims;
      if (width !== reqW || height !== reqH) {
        return `${file.name} must be ${reqW}x${reqH} pixels`;
      }
    } catch {
      return `Failed to validate dimensions for ${file.name}`;
    }

    return null;
  };

  const onPickMultiFiles = async (files, setFiles, type, existingNames) => {
    const selected = Array.from(files || []);
    if (!selected.length) return [];
    const existingCount = (existingNames || []).length;
    if (existingCount >= MAX_CHARACTER_IMAGES) {
      Swal.fire(
        "Limit reached",
        `You can upload a maximum of ${MAX_CHARACTER_IMAGES} ${type} images.`,
        "warning"
      );
      return [];
    }

    const nextTotal = existingCount + selected.length;
    if (nextTotal > MAX_CHARACTER_IMAGES) {
      Swal.fire(
        "Limit exceeded",
        `Only ${MAX_CHARACTER_IMAGES - existingCount} more ${type} image(s) can be added. Maximum is ${MAX_CHARACTER_IMAGES}.`,
        "warning"
      );
      return [];
    }

    const validationResults = await Promise.all(
      selected.map(async (file) => ({
        file,
        error: await validateImageByType(file, type),
      }))
    );
    const errors = validationResults.map((item) => item.error).filter(Boolean);
    if (errors.length) {
      Swal.fire("Invalid file", errors[0], "error");
      return [];
    }

    const renamed = selected.map(renameFileForUpload);
    setFiles((prev) => [...prev, ...renamed]);
    return renamed;
  };

  const onPickSingleFile = async (file, setFile, type) => {
    if (!file) return;
    const error = await validateImageByType(file, type);
    if (error) {
      Swal.fire("Invalid file", error, "error");
      return;
    }
    setFile(renameFileForUpload(file));
  };

  const removeByIndex = (listSetter, index) => {
    listSetter((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      const pendingGoodNames = new Set((goodFiles || []).map((f) => f?.name).filter(Boolean));
      const pendingBadNames = new Set((badFiles || []).map((f) => f?.name).filter(Boolean));
      const persistedGoodNames = (goodNames || []).filter((name) => !pendingGoodNames.has(name));
      const persistedBadNames = (badNames || []).filter((name) => !pendingBadNames.has(name));

      
      formData.append("themeName", currentTheme);
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName || "",
          themeDescription: data?.themeDescription || "",
          good: persistedGoodNames,
          bad: persistedBadNames,
          game_background_desk: gameDeskName,
          game_background_mob: gameMobName,
          timer: data?.timer || "05:00",
        })
      );
  
      goodFiles.forEach((file) => formData.append("goodFiles", file));
      badFiles.forEach((file) => formData.append("badFiles", file));
      if (gameDeskFile) formData.append("game_background_desk", gameDeskFile);
      if (gameMobFile) formData.append("game_background_mob", gameMobFile);
      await uploadThemeMedia({ formData, token: adminToken });

      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setGoodFiles([]);
      setBadFiles([]);
      setGameDeskFile(null);
      setGameMobFile(null);
      Swal.fire("Success", "Theme data updated", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update theme data", "error");
    } finally {
      setSaving(false);
    }
  };

  const getCharacterListImgSrc = (name, blobByName) =>
    (name && blobByName[name]) || getPreviewUrl(name);

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/rules")}> 
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="theme-data-page">
        <div className="theme-data-grid">
          <section className="theme-data-card">
            <h3>Good Images</h3>
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={async (e) => {
                const selectedFiles = Array.from(e.target.files || []);
                const validFiles = await onPickMultiFiles(
                  selectedFiles,
                  setGoodFiles,
                  "good",
                  goodNames
                );
                setGoodNames((prev) => mergeNamesWithFiles(prev, validFiles));
                e.target.value = "";
              }}
            />
            <div className="theme-data-helper-text">Required: 300x300 px, max 2MB, JPG/JPEG/PNG</div>
            <div className="theme-data-helper-text">Maximum 15 good images allowed.</div>
            {goodFiles.length > 0 && (
              <div className="theme-data-pending">
                Pending upload: {goodFiles.map((f) => f.name).join(", ")}
              </div>
            )}
            <div className="theme-data-list">
              {goodNames.map((name, index) => (
                <div className="theme-data-item" key={`${name}-${index}`}>
                  <img
                    src={getCharacterListImgSrc(name, goodBlobSrcByName)}
                    alt={name}
                  />
                  <span>{name}</span>
                  <button onClick={() => removeByIndex(setGoodNames, index)}>&times;</button>
                </div>
              ))}
            </div>
          </section>

          <section className="theme-data-card">
            <h3>Bad Images</h3>
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={async (e) => {
                const selectedFiles = Array.from(e.target.files || []);
                const validFiles = await onPickMultiFiles(
                  selectedFiles,
                  setBadFiles,
                  "bad",
                  badNames
                );
                setBadNames((prev) => mergeNamesWithFiles(prev, validFiles));
                e.target.value = "";
              }}
            />
            <div className="theme-data-helper-text">Required: 300x300 px, max 2MB, JPG/JPEG/PNG</div>
            <div className="theme-data-helper-text">Maximum 15 bad images allowed.</div>
            {badFiles.length > 0 && (
              <div className="theme-data-pending">
                Pending upload: {badFiles.map((f) => f.name).join(", ")}
              </div>
            )}
            <div className="theme-data-list">
              {badNames.map((name, index) => (
                <div className="theme-data-item" key={`${name}-${index}`}>
                  <img
                    src={getCharacterListImgSrc(name, badBlobSrcByName)}
                    alt={name}
                  />
                  <span>{name}</span>
                  <button onClick={() => removeByIndex(setBadNames, index)}>&times;</button>
                </div>
              ))}
            </div>
          </section>

          <section className="theme-data-card">
            <h3>Game Backgrounds</h3>

            <div className="theme-data-bg-row">
              <label>Desktop Background</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => onPickSingleFile(e.target.files?.[0], setGameDeskFile, "gameDesk")}
              />
              <div className="theme-data-helper-text">Required: 1920x1080 px, max 5MB, JPG/JPEG/PNG</div>
              <div className="theme-data-bg-preview theme-data-bg-preview-desktop">
                <img
                  src={deskBlobSrc || getPreviewUrl(gameDeskName)}
                  alt="Desktop background"
                />
              </div>
              <div className="theme-data-bg-name">{gameDeskFile ? gameDeskFile.name : gameDeskName}</div>
            </div>

            <div className="theme-data-bg-row">
              <label>Mobile Background</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => onPickSingleFile(e.target.files?.[0], setGameMobFile, "gameMob")}
              />
              <div className="theme-data-helper-text">Required: 414x896 px, max 5MB, JPG/JPEG/PNG</div>
              <div className="theme-data-bg-preview theme-data-bg-preview-mobile">
                <img
                  src={mobBlobSrc || getPreviewUrl(gameMobName)}
                  alt="Mobile background"
                />
              </div>
              <div className="theme-data-bg-name">{gameMobFile ? gameMobFile.name : gameMobName}</div>
            </div>
          </section>

        </div>

        <div className="theme-data-actions">
          <button className="save-and-continue" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}

export default ThemeUpdate;
